// src/pages/admin/AdminOrdersManagement.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  FiShoppingBag,
  FiFileText,
  FiSearch,
  FiDownload,
  FiEye,
  FiPaperclip,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiDollarSign,
  FiCalendar,
  FiPackage,
  FiTrendingUp,
  FiChevronLeft,
  FiChevronRight,
  FiPhone,
  FiShare2,
  FiUser,
  FiEdit2,
  FiCopy,
  FiSend,
  FiRotateCcw,
  FiPlus,
  FiAlertTriangle,
  FiArchive,
  FiTrash2,
  FiInbox, FiTag
} from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import { AdminInvoicesPanel } from "./AdminInvoices";
import { toast } from "react-toastify";
import CreateOrderModal from "../../components/orders/CreateOrderModal";
import InvoiceActions from "../../components/orders/InvoiceActions";
import ShareOrderModal from "../../components/orders/ShareOrderModal";
import EditOrderModal from "../../components/orders/EditOrderModal";
import RefundModal from "../../components/orders/RefundModal";
import { useAuth } from "../../context/AuthContext";
// v1.464.0 — CORRECTIF : `orderAPI` etait utilise sans etre importe.
//
// Le fichier n'importait que `API` (l'instance axios), mais trois appels
// passent par `orderAPI` : setArchived, remove, staffList. A l'execution,
// JavaScript levait « orderAPI is not defined » AVANT tout envoi reseau.
//
// Cette erreur n'a pas de `response`, et mon message de diagnostic la classait
// donc parmi les pannes reseau — ce qui m'a fait chercher du cote du serveur,
// de l'hebergeur, des methodes HTTP et des extensions pendant des heures,
// alors que rien n'avait jamais quitte le navigateur.
//
// Le controle `e instanceof TypeError` que j'avais ajoute ne pouvait pas la
// voir : « x is not defined » est une ReferenceError, pas une TypeError. Les
// deux sont desormais traitees ensemble.
import API, { orderAPI } from "../../services/api";
import normalizeImagePath from "../../utils/imageUtils";

const safeLower = (v) => String(v ?? "").toLowerCase();

const readTotalFromResponse = (res) => {
  try {
    const payload = res?.data ?? {};

    if (payload.totalItems ?? payload.total) return Number(payload.totalItems ?? payload.total);
    if (payload.meta && (payload.meta.total ?? payload.meta.totalItems)) return Number(payload.meta.total ?? payload.meta.totalItems);
    if (payload.pagination && (payload.pagination.total ?? payload.pagination.totalItems)) return Number(payload.pagination.total ?? payload.pagination.totalItems);

    // si payload.data est un objet pagination
    if (payload.data && !Array.isArray(payload.data)) {
      if (payload.data.total ?? payload.data.totalItems) return Number(payload.data.total ?? payload.data.totalItems);
    }

    // header
    const headerTotal = res?.headers?.["x-total-count"] || res?.headers?.["X-Total-Count"];
    if (headerTotal) return Number(headerTotal);
  } catch {}
  return 0;
};

const generateCSV = (data) => {
  const headers = ["Numéro", "Client", "Email", "Téléphone", "Total", "Statut paiement", "Statut commande", "Date"];
  const rows = data.map((order) => {
    const num = order.orderNumber || (order._id ? order._id.slice(-6) : "N/A");
    const client = `${order.customer?.firstName || ""} ${order.customer?.lastName || ""}`.trim() || "N/A";
    const email = order.customer?.email || "N/A";
    const phone = order.customer?.phone || "";
    const total = typeof order.total === "number" ? `${order.total} MAD` : order.total || "0 MAD";
    const paymentStatus = order.paymentStatus || "N/A";
    const status = order.status || "N/A";
    const date = order.createdAt ? new Date(order.createdAt).toLocaleDateString("fr-FR") : "";
    return [num, client, email, phone, total, paymentStatus, status, date];
  });

  const escapeCell = (cell) => {
    const s = String(cell ?? "");
    if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  return [headers, ...rows].map((r) => r.map(escapeCell).join(",")).join("\n");
};

const downloadCSV = (content, filename) => {
  try {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 800);
  } catch {
    toast.error("Impossible de télécharger le CSV");
  }
};

// ---- date helpers (front) ----
const isSameDay = (d1, d2) => {
  try {
    const a = new Date(d1);
    const b = new Date(d2);
    return a.toDateString() === b.toDateString();
  } catch {
    return false;
  }
};

// v1.442.0 — isWithinLastDays() a ete retire : il ne servait qu'au filtrage
// des dates DANS LA PAGE COURANTE, remplace par le filtre serveur. Le laisser
// aurait casse le build (react-scripts compile avec CI=true et transforme
// l'avertissement « defini mais jamais utilise » en erreur bloquante).

export default function AdminOrdersManagement() {
  const [orders, setOrders] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  // Commande en cours de modification (id + numero, pour l'affichage).
  const [editOrder, setEditOrder] = useState(null);
  // Remboursement : { mode: 'request'|'decide'|'undo', order }
  const [refund, setRefund] = useState(null);
  // Seul l'admin decide ou annule. Le serveur le revalide de toute facon —
  // masquer un bouton ne protege rien, ca evite juste un clic pour rien.
  const { user: currentUser } = useAuth() || {};
  const isPlatformAdmin = String(currentUser?.role || '') === 'admin';
  // Panier pre-rempli pour dupliquer une commande (meme client, memes offres).
  const [duplicate, setDuplicate] = useState(null);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // paymentStatus
  const [dateFilter, setDateFilter] = useState("all"); // today/week/month

  // Pré-remplit la recherche depuis l'URL (?order=...) — clic depuis le dashboard
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") === "invoices" ? "invoices" : "orders"
  );
  useEffect(() => {
    const o = searchParams.get("order") || searchParams.get("q");
    if (o) setSearchTerm(o);
    if (searchParams.get("tab") === "invoices") setActiveTab("invoices");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "invoices") setActiveTab("invoices");
    else if (tab === "orders") setActiveTab("orders");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const [selectedOrder, setSelectedOrder] = useState(null);
  // v1.442.0 — totaux calcules par le SERVEUR sur l'ensemble du filtre.
  // Null tant qu'ils ne sont pas arrives (ou si le serveur n'est pas encore a
  // jour) : l'ecran retombe alors sur l'ancien calcul de page.
  const [serverStats, setServerStats] = useState(null);
  // v1.444.0 — archivage / suppression
  const [vueArchives, setVueArchives] = useState(false);
  // v1.445.0 — filtre partenaire. La liste vient du serveur, calculee sur les
  // commandes existantes : on ne propose jamais un partenaire sans commande.
  const [merchantFilter, setMerchantFilter] = useState('');
  // v1.407.0 — etat du coupon : '' | used | unused | completed_unused
  const [couponFilter, setCouponFilter] = useState('');
  const [partners, setPartners] = useState([]);
  const [removeFor, setRemoveFor] = useState(null);   // commande visée
  const [removeInfo, setRemoveInfo] = useState(null); // droits renvoyés par le serveur
  const [removeReason, setRemoveReason] = useState('');
  const [removeBusy, setRemoveBusy] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  // Numéro de la commande à partager (WhatsApp / email / impression)
  const [shareOrderNumber, setShareOrderNumber] = useState(null);

  const mountedRef = useRef(true);
  const abortRef = useRef(null);
  const searchDebounce = useRef(null);
  // Premier rendu : l'effet des filtres ne doit pas relancer une requete que
  // l'effet de pagination vient deja de lancer. Sans ce garde-fou, chaque
  // ouverture de l'ecran declenchait DEUX appels a /admin/orders.
  const filtresPremierRendu = useRef(true);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(searchDebounce.current);
      try {
        abortRef.current?.abort();
      } catch {}
    };
  }, []);

  const getStatusBadge = (status) => {
    const styles = {
      completed: "bg-green-100 text-green-800",
      pending: "bg-yellow-100 text-yellow-800",
      cancelled: "bg-red-100 text-red-800",
      refunded: "bg-red-100 text-red-800",
      refund_pending: "bg-amber-100 text-amber-800",
      failed: "bg-red-100 text-red-800",
      processing: "bg-blue-100 text-blue-800"
    };
    const labels = {
      completed: "Payée",
      pending: "En attente",
      cancelled: "Annulée",
      refunded: "Remboursée",
      refund_pending: "Remb. en attente",
      failed: "Échoué",
      processing: "En cours"
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status] || "bg-gray-100 text-gray-800"}`}>
        {labels[status] || status || "—"}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <FiCheckCircle className="text-green-500" />;
      case "pending":
        return <FiClock className="text-yellow-500" />;
      case "cancelled":
      case "refunded":
      case "failed":
        return <FiXCircle className="text-red-500" />;
      case "processing":
        return <FiPackage className="text-blue-500" />;
      default:
        return <FiClock className="text-gray-500" />;
    }
  };

  // DUPLIQUER — le client revient et reprend la meme chose.
  //
  // On ouvre le formulaire de creation PRE-REMPLI plutot que de copier la
  // commande en base : l'agent garde la main sur la quantite, le moyen de
  // paiement et la remise, et rien n'est enregistre tant qu'il n'a pas validé.
  // Dupliquer en silence creerait des commandes fantomes au moindre clic.
  // MODE DE PAIEMENT — libelle lisible au comptoir.
  //
  // La base stocke des codes techniques ('tpe', 'payexpress', 'cash'...). Les
  // afficher tels quels obligerait l'agent a les traduire de tete. 'pending'
  // n'est pas un moyen de paiement : c'est une commande enregistree sans
  // encaissement, et le dire evite qu'on la croie reglee.
  const PAYMENT_LABEL = {
    cash: 'Espèce',
    tpe: 'TPE',
    virement: 'Virement',
    payzone: 'Payzone',
    cmi: 'CMI',
    card: 'Carte',
    wallet: 'Portefeuille',
    payexpress: 'PayExpress',
    pending: 'Non encaissée',
  };
  const PAYMENT_STYLE = {
    cash: 'bg-green-50 text-green-700 border-green-200',
    tpe: 'bg-blue-50 text-blue-700 border-blue-200',
    virement: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    payexpress: 'bg-amber-50 text-amber-800 border-amber-200',
    pending: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  const paymentBadge = (m) => {
    const k = String(m || '').toLowerCase();
    if (!k) return <span className="text-sm text-gray-400">—</span>;
    return (
      <span className={`text-xs px-2 py-1 rounded-full border font-medium whitespace-nowrap ${PAYMENT_STYLE[k] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
        {PAYMENT_LABEL[k] || m}
      </span>
    );
  };

  const duplicateOrder = (order) => {
    const items = Array.isArray(order.items) ? order.items : [];
    if (!items.length) {
      toast.error("Cette commande n'a aucune ligne à dupliquer");
      return;
    }
    setDuplicate({
      customer: {
        name: order.guestName || (order.customer ? `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim() : ''),
        phone: order.guestPhone || order.customer?.phone || '',
        email: order.guestEmail || order.customer?.email || '',
      },
      lines: items.map((it) => ({
        dealId: it.dealId,
        title: it.title || it.deal?.title || 'Offre',
        price: Number(it.price) || 0,
        quantity: Number(it.quantity) || 1,
      })),
    });
  };

  const fetchOrders = useCallback(
    async (p = page, l = limit, q = searchTerm, status = statusFilter, date = dateFilter, merchant = merchantFilter) => {
      if (mountedRef.current) setLoading(true);

      try {
        try {
          abortRef.current?.abort();
        } catch {}
        abortRef.current = new AbortController();

        const params = { page: p, limit: l };
      // v1.444.0 — la vue « Archivées » demande explicitement les archives.
      if (vueArchives) params.archived = 1;
      if (merchant) params.merchantId = merchant;
      if (couponFilter) params.coupon = couponFilter;
        if (q?.trim()) params.q = q.trim();
        if (status && status !== "all") params.paymentStatus = status;
        if (date && date !== "all") params.date = date; // si ton backend gère date

        const res = await API.get("/admin/orders", {
          params,
          signal: abortRef.current.signal
        });

        const payload = res?.data ?? {};
        const list =
          Array.isArray(payload.data) ? payload.data :
          Array.isArray(payload.orders) ? payload.orders :
          Array.isArray(payload.items) ? payload.items :
          [];

        if (mountedRef.current) {
          setOrders((list || []).map((o) => ({ ...o, customer: o.customer || ((o.guestName || o.guestPhone || o.guestEmail) ? { firstName: o.guestName, lastName: '', email: o.guestEmail, phone: o.guestPhone } : null) })));
          setTotal(readTotalFromResponse(res) || payload.total || payload.totalItems || list.length || 0);
          setServerStats(payload.stats || null);
          // Conservee si la reponse n'en renvoie pas (serveur plus ancien) :
          // sinon le selecteur se viderait a chaque rechargement.
          if (Array.isArray(payload.partners)) setPartners(payload.partners);
        }
      } catch (err) {
        const isAbort =
          err?.name === "AbortError" ||
          err?.code === "ERR_CANCELED" ||
          err?.message === "canceled";

        if (isAbort) return;

        console.error("fetchOrders error:", err);
        // Le message generique « Erreur lors du chargement des commandes » est
        // le meme pour une route absente, une colonne manquante en base et une
        // session expiree — trois causes, trois corrections differentes. On
        // affiche donc ce que le serveur repond, c'est un ecran d'administration.
        const st = err?.response?.status;
        const body = err?.response?.data || {};
        let msg;
        if (st === 404) {
          msg = "Route /api/admin/orders introuvable : le fichier routes/adminRoutes.js du serveur n'est pas à jour.";
        } else if (st === 401 || st === 403) {
          msg = "Accès refusé : reconnectez-vous avec un compte administrateur.";
        } else if (!err?.response) {
          msg = "Serveur injoignable : l'API ne répond pas.";
        } else {
          msg = body.message || `Erreur ${st} lors du chargement des commandes`;
          if (body.hint) msg += ` — ${body.hint}`;
        }
        toast.error(msg, { duration: 8000 });

        if (mountedRef.current) {
          setOrders([]);
          setTotal(0);
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    // ============================================================
    // v1.442.0 — `page` RETIRE DES DEPENDANCES (correctif pagination)
    //
    // `page` figurait ici, donc l'identite de fetchOrders changeait a chaque
    // changement de page. L'effet de recherche ci-dessous ayant `fetchOrders`
    // dans ses dependances, il se redeclenchait — et son minuteur appelait
    // setPage(1) 350 ms plus tard. Cliquer sur « page suivante » chargeait
    // donc bien la page 2, puis revenait AUTOMATIQUEMENT en page 1 : la
    // pagination paraissait morte alors qu'elle fonctionnait une fraction de
    // seconde.
    //
    // Les valeurs par defaut des parametres ne servent qu'aux appels sans
    // argument ; tous les appelants passent leurs valeurs explicitement, donc
    // retirer `page` ne prive personne de rien.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [limit, searchTerm, statusFilter, dateFilter, vueArchives, merchantFilter, couponFilter]
  );

  // Changement de page ou de taille de page : on recharge, sans toucher aux
  // filtres.
  useEffect(() => {
    fetchOrders(page, limit, searchTerm, statusFilter, dateFilter, merchantFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  // Recherche et filtres : retour en page 1, c'est voulu — rester en page 7
  // d'un resultat qui n'en compte plus que 2 afficherait un tableau vide.
  //
  // `fetchOrders` NE DOIT PAS figurer dans ces dependances : ce serait
  // remettre en place la boucle decrite ci-dessus, par un autre chemin.
  useEffect(() => {
    if (filtresPremierRendu.current) {
      filtresPremierRendu.current = false;
      return undefined;
    }
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setPage(1);
      fetchOrders(1, limit, searchTerm, statusFilter, dateFilter, merchantFilter);
    }, 350);

    return () => clearTimeout(searchDebounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, dateFilter, limit, vueArchives, merchantFilter, couponFilter]);

  const handleStatusChange = async (orderId, payload) => {
    if (!orderId || !payload) return;

    setIsSubmitting(true);
    try {
      const res = await API.put(`/admin/orders/${orderId}/status`, payload);
      toast.success("Statut mis à jour");
      // Met à jour la commande ouverte dans la modale (sinon le bouton « Confirmer »
      // resterait affiché et le statut paiement figé sur « En attente »).
      const updated = (res && res.data && res.data.data && res.data.data.order) || null;
      setSelectedOrder((prev) => {
        if (!prev || String(prev.id || prev._id) !== String(orderId)) return prev;
        return updated ? { ...prev, ...updated } : { ...prev, ...payload };
      });
      await fetchOrders(page, limit, searchTerm, statusFilter, dateFilter, merchantFilter);
    } catch (e) {
      console.error("handleStatusChange error:", e);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  // ---- client-side filtering (en plus du backend) ----
  const filteredOrders = useMemo(() => {
    let list = Array.isArray(orders) ? orders : [];

    const term = safeLower(searchTerm).trim();
    if (term) {
      list = list.filter((o) => {
        const num = safeLower(o.orderNumber || o._id || "");
        const client = safeLower(`${o.customer?.firstName || ""} ${o.customer?.lastName || ""}`.trim());
        const email = safeLower(o.customer?.email || "");
        const phone = safeLower(o.customer?.phone || "");
        return num.includes(term) || client.includes(term) || email.includes(term) || phone.includes(term);
      });
    }

    if (statusFilter && statusFilter !== "all") {
      list = list.filter((o) => safeLower(o.paymentStatus) === safeLower(statusFilter));
    }

    // v1.442.0 — le filtre de DATE est desormais applique par le serveur
    // (parametre `date`), sur toute la base. Le refiltrer ici serait au mieux
    // inutile, au pire faussement rassurant : cela ne triait que les lignes
    // deja recues, donc uniquement la page affichee.

    return list;
  }, [orders, searchTerm, statusFilter, dateFilter]);

  // ============================================================
  // v1.444.0 — RETIRER UNE COMMANDE DE LA LISTE
  // On demande d'abord au serveur ce qui est permis, PUIS on ouvre la fenêtre.
  // L'inverse ferait découvrir un refus après avoir tapé motif et confirmation.
  // ============================================================
  // ============================================================
  // v1.455.0 — DIRE CE QUI S'EST REELLEMENT PASSE
  //
  // Les deux messages precedents — « Action impossible » et « Impossible de
  // verifier » — jetaient la reponse du serveur et n'apprenaient rien. Il
  // fallait ouvrir les outils de developpement pour connaitre le code HTTP,
  // ce qui n'est pas raisonnable a demander en exploitation.
  //
  // Chaque code a une cause unique et un fichier a deployer : on les nomme.
  // ============================================================
  const decrireErreur = (e, msEcoulees) => {
    // DUREE MESUREE PAR L'ECRAN.
    //
    // « Instantané » et « après 30 secondes » ont des causes opposées —
    // requête bloquée d'un côté, base saturée de l'autre — mais produisaient le
    // même message. Plutôt que de demander de chronométrer à la main, on
    // mesure et on l'affiche.
    const duree = typeof msEcoulees === 'number'
      ? ` [réponse en ${(msEcoulees / 1000).toFixed(1)} s]` : '';
    const st = e?.response?.status;
    const msg = e?.response?.data?.message;

    // v1.456.0 — DISTINGUER « PAS DE RESPONSE » DE « PAS DE REQUETE ».
    //
    // La version precedente concluait « le serveur n'a pas repondu » des que
    // `e.response` etait absent — ce qui est vrai pour une panne reseau, mais
    // AUSSI pour une simple TypeError levee dans le navigateur avant tout
    // envoi. C'est le cas quand services/api.js n'est pas a jour : appeler
    // `orderAPI.removable()` sur l'ancienne version leve
    // « removable is not a function », aucune requete ne part, et le message
    // accusait le serveur a tort.
    // ReferenceError ET TypeError : les deux signalent une erreur de code dans
    // le navigateur, jamais un probleme de serveur. Ne traiter que TypeError
    // laissait « x is not defined » se faire passer pour une panne reseau.
    if (e instanceof TypeError || e instanceof ReferenceError
        || /is not (a function|defined)/.test(String(e?.message))) {
      return "Erreur de code côté navigateur — aucune requête n'a été envoyée. "
        + `(${e.name} : ${e.message})` + duree;
    }
    // DELAI DEPASSE — axios abandonne au bout de 30 s (timeout configure dans
    // services/api.js). Ce n'est pas un blocage reseau : la requete est bien
    // partie, le serveur n'a simplement pas fini a temps. Sur cet hebergement,
    // la cause habituelle est la saturation de la base (« max_user_connections »,
    // « Lock wait timeout » dans les journaux).
    if (e?.code === 'ECONNABORTED' || /timeout/i.test(String(e?.message))) {
      return "Le serveur n'a pas répondu dans les 30 secondes. La requête est bien partie, "
        + "mais la base de données n'a pas fini à temps — généralement une saturation "
        + "(connexions MySQL). Réessayez dans un instant." + duree;
    }
    // v1.464.0 — DETAIL TECHNIQUE BRUT.
    //
    // Les hypotheses successives (methode HTTP, mot filtre dans l'URL,
    // extension du navigateur) ont toutes ete ecartees par les tests. Seul le
    // navigateur detient encore l'information : on l'affiche telle quelle,
    // plutot que de continuer a supposer.
    //
    // `e.code` (ERR_NETWORK, ERR_CANCELED, ECONNABORTED...), le message natif
    // et l'URL reellement appelee suffisent a nommer la cause.
    if (!e?.response) {
      const cfg = e?.config || {};
      const cible = `${String(cfg.method || '?').toUpperCase()} ${cfg.baseURL || ''}${cfg.url || ''}`;
      return "Aucune réponse du serveur." + duree
        + `\ncode : ${e?.code || '(aucun)'}`
        + `\nmessage : ${e?.message || '(aucun)'}`
        + `\nappel : ${cible}`;
    }
    if (st === 404) {
      return "404 — la route n'existe pas sur le serveur. Le fichier routes/adminRoutes.js "
        + "déployé ne contient pas les routes d'archivage, ou le serveur n'a pas redémarré "
        + "(touch tmp/restart.txt)." + duree;
    }
    if (st === 503) {
      return `503 — ${msg || "contrôleur absent : copiez controllers/orderArchiveController.js puis redémarrez."}` + duree;
    }
    if (st === 500) {
      return `500 — ${msg || "erreur serveur. Les colonnes orders.archived sont probablement absentes : redémarrez pour qu'ensureSchema les crée."}`;
    }
    if (st === 403) return `403 — ${msg || "accès refusé : permission manquante sur ce compte."}`;
    return `${st} — ${msg || 'erreur inconnue'}` + duree;
  };

  // v1.464.0 — PLUS AUCUN APPEL A L'OUVERTURE.
  //
  // La fenetre interrogeait GET /admin/orders/:id/removable. Cet appel
  // echouait sans jamais atteindre l'application, alors que la meme URL
  // repondait correctement dans la barre d'adresse — impossible a diagnostiquer
  // a distance.
  //
  // Le verdict voyage maintenant avec la liste des commandes, qui fonctionne.
  // La fenetre s'ouvre donc instantanement, sans reseau, et ne peut plus
  // echouer a l'ouverture.
  const openRemove = (order) => {
    const id = order._id || order.id;
    setRemoveFor({ id, number: order.orderNumber, archived: !!order.archived });
    setRemoveReason('');
    setRemoveInfo({
      archived: !!order.archived,
      canArchive: true,
      // `canDelete` absent (serveur pas encore a jour) : on refuse la
      // suppression par prudence, l'archivage reste propose.
      canDelete: order.canDelete === true,
      bloquants: Array.isArray(order.bloquants) && order.bloquants.length
        ? order.bloquants
        : (order.canDelete === true ? [] : ["Vérification indisponible — seul l'archivage est proposé."]),
    });
  };

  const doArchive = async (archived) => {
    if (!removeFor) return;
    const depart = Date.now();
    try {
      setRemoveBusy(true);
      await orderAPI.setArchived(removeFor.id, archived, removeReason.trim());
      toast.success(archived ? 'Commande archivée' : 'Commande remise dans la liste');
      setRemoveFor(null);
      fetchOrders(page, limit, searchTerm, statusFilter, dateFilter, merchantFilter);
    } catch (e) {
      const d = decrireErreur(e, Date.now() - depart);
      setRemoveInfo((prev) => ({ ...(prev || {}), erreur: d }));
      toast.error(d, { duration: 10000 });
    } finally { setRemoveBusy(false); }
  };

  const doDelete = async () => {
    if (!removeFor) return;
    if (removeReason.trim().length < 5) return toast.error('Indiquez le motif (5 caractères minimum).');
    try {
      setRemoveBusy(true);
      await orderAPI.remove(removeFor.id, removeReason.trim());
      toast.success(`Commande ${removeFor.number} supprimée`);
      setRemoveFor(null);
      fetchOrders(page, limit, searchTerm, statusFilter, dateFilter, merchantFilter);
    } catch (e) {
      const d = e?.response?.data;
      // Le serveur renvoie la liste des raisons : on les affiche toutes plutôt
      // qu'un « action impossible » qui n'apprend rien.
      if (Array.isArray(d?.bloquants) && d.bloquants.length) {
        setRemoveInfo((prev) => ({ ...(prev || {}), canDelete: false, bloquants: d.bloquants }));
        toast.error(d.message || 'Suppression refusée');
      } else {
        toast.error(d?.message || 'Suppression impossible');
      }
    } finally { setRemoveBusy(false); }
  };

  const stats = useMemo(() => {
    // ============================================================
    // v1.442.0 — LES TOTAUX VIENNENT DU SERVEUR
    //
    // Ce bloc calculait « Revenu Total » a partir de `orders`, qui ne contient
    // que LA PAGE COURANTE (20 lignes). La carte affichait donc la somme de 20
    // commandes en l'appelant « Revenu Total » — d'ou l'ecart avec le tableau
    // de bord, qui agrege cote serveur sur toute la periode.
    //
    // Le repli sur le calcul de page est conserve pour le cas ou le serveur
    // n'est pas encore deploye : mieux vaut un chiffre partiel qu'un ecran
    // vide, mais le serveur prime des qu'il repond.
    // ============================================================
    if (serverStats) {
      return {
        total: Number(serverStats.total) || 0,
        today: Number(serverStats.today) || 0,
        revenue: Number(serverStats.revenue) || 0,
        average: Number(serverStats.average) || 0,
        completed: Number(serverStats.completed) || 0,
        pending: Number(serverStats.pending) || 0,
      };
    }

    const all = Array.isArray(orders) ? orders : [];
    const totalItems = all.length;

    const todayCount = all.filter((o) => o.createdAt && isSameDay(o.createdAt, new Date())).length;

    const completed = all.filter((o) => o.paymentStatus === "completed");
    const revenue = completed.reduce((s, o) => s + (Number(o.total) || 0), 0);
    const average = completed.length ? revenue / completed.length : 0;

    const pending = all.filter((o) => o.paymentStatus === "pending").length;

    return {
      total: totalItems,
      today: todayCount,
      revenue,
      average,
      completed: completed.length,
      pending
    };
  }, [orders, serverStats]);

  const totalPages = Math.max(1, Math.ceil((total || filteredOrders.length) / (limit || 1)));
  const goToPage = (p) => setPage(Math.max(1, Math.min(totalPages, p)));

  const handleBulkExport = () => {
    downloadCSV(generateCSV(filteredOrders), "orders-export.csv");
    toast.success("Export réussi !");
  };

  const tabsBar = (
    <div className="flex items-center gap-1 border-b border-gray-200">
      {[
        { key: "orders", label: "Commandes", icon: FiShoppingBag },
        { key: "invoices", label: "Factures", icon: FiFileText }
      ].map((t) => {
        const Icon = t.icon;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === t.key
                ? "border-primary-600 text-primary-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <Icon size={16} /> {t.label}
          </button>
        );
      })}
    </div>
  );

  if (activeTab === "invoices") {
    return (
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion des Commandes</h1>
              <p className="text-gray-600 mt-1">Commandes et factures de la plateforme</p>
            </div>
          </div>
          {tabsBar}
        </div>
        <AdminInvoicesPanel />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header + filtres */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestion des Commandes</h1>
            <p className="text-gray-600 mt-1">Suivez et gérez toutes les commandes clients</p>
          </div>

          <div className="flex items-center space-x-2">
            {/* Création manuelle d'une commande (client qui se présente au bureau).
                Le modal existait déjà et était monté plus bas, mais AUCUN bouton ne
                l'ouvrait côté admin — la fonction était donc inatteignable.
                Le dashboard marchand, lui, avait bien son bouton. */}
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="flex items-center px-4 py-2 bg-[#e11d7b] text-white rounded-lg hover:opacity-90 transition-colors"
            >
              <FiPlus className="mr-2" />
              Créer une commande
            </button>
            <button
              type="button"
              onClick={handleBulkExport}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <FiDownload className="mr-2" />
              Exporter CSV
            </button>
          </div>
        </div>

        {tabsBar}
        <div className="mt-4" />

        <div className="bg-white rounded-lg shadow p-4">
          {/* v1.445.0 — 5 colonnes au lieu de 4 : la recherche en occupe 2, et
              le filtre partenaire s'ajoute aux filtres statut et date. Rester
              a 4 aurait renvoye le nouveau selecteur a la ligne, seul. */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par numéro, client, email, téléphone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="completed">Payées</option>
              <option value="pending">En attente</option>
              <option value="processing">En cours</option>
              <option value="failed">Paiement échoué</option>
              <option value="cancelled">Annulées</option>
              <option value="refunded">Remboursées</option>
              <option value="refund_pending">Remboursement en attente</option>
            </select>

            <select
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">Toutes les dates</option>
              <option value="today">Aujourd&apos;hui</option>
              <option value="week">7 derniers jours</option>
              <option value="month">30 derniers jours</option>
            </select>
          </div>

          {/* v1.445.0 — FILTRE PARTENAIRE
              Le nombre de commandes est affiche a cote de chaque nom : on voit
              d'un coup d'oeil quel partenaire pese le plus, sans ouvrir sa
              fiche. La liste ne contient que des partenaires ayant des
              commandes dans le perimetre courant. */}
          <div>
            <select
              value={merchantFilter}
              onChange={(e) => { setMerchantFilter(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Tous les partenaires</option>
              {partners.map((pa) => (
                <option key={pa.id} value={pa.id}>
                  {pa.name}{typeof pa.count === 'number' ? ` (${pa.count})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* v1.444.0 — bascule vers les commandes archivees.
            Presentee comme une VUE et non comme un filtre de plus : on n'est
            jamais dans les deux a la fois, et l'ecran doit dire clairement
            lequel on regarde. */}
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => { setVueArchives(false); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-sm border inline-flex items-center gap-1.5 ${
              !vueArchives ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300'}`}
          >
            <FiInbox size={13} /> Commandes actives
          </button>
          <button
            type="button"
            onClick={() => { setVueArchives(true); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-sm border inline-flex items-center gap-1.5 ${
              vueArchives ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300'}`}
          >
            <FiArchive size={13} /> Archivées
          </button>
          {vueArchives && (
            <span className="text-xs text-gray-500">
              Ces commandes sont masquées de la liste principale. Rien n&apos;a été supprimé.
            </span>
          )}
        </div>

        {/* v1.407.0 — ETAT DU COUPON. « Terminée » est pose a la main et ne
            prouve pas que le client est venu : seule la VALIDATION DU COUPON
            le prouve, et c'est elle qui compte la marge Maxula et l'argent du
            partenaire. */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 inline-flex items-center gap-1"><FiTag size={12} /> Coupon :</span>
          {[
            { v: '', l: 'Tous' },
            { v: 'used', l: 'Coupon validé' },
            { v: 'unused', l: 'Coupon non utilisé' },
            { v: 'completed_unused', l: 'Terminée sans coupon validé' },
          ].map((f) => (
            <button key={f.v || 'all'} type="button"
              onClick={() => { setCouponFilter(f.v); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-sm border ${
                couponFilter === f.v
                  ? (f.v === 'completed_unused' ? 'bg-amber-600 text-white border-amber-600' : 'bg-gray-900 text-white border-gray-900')
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
              {f.l}
              {f.v === 'completed_unused' && Number(serverStats?.completedUnused) > 0 ? ` (${serverStats.completedUnused})` : ''}
            </button>
          ))}
        </div>
        {Number(serverStats?.completedUnused) > 0 && couponFilter !== 'completed_unused' && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-2">
            <span className="inline-flex items-center gap-2">
              <FiAlertTriangle size={14} />
              <strong>{serverStats.completedUnused}</strong> commande(s) « Terminée » dont le coupon n&apos;a pas été validé :
              paiement du partenaire en attente (la marge Maxula est déjà comptée).
            </span>
            <button type="button" onClick={() => { setCouponFilter('completed_unused'); setPage(1); }}
              className="text-xs font-semibold underline">Voir la liste</button>
          </div>
        )}
      </div>

      {/* Cards stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Commandes</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FiShoppingBag className="text-blue-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Aujourd&apos;hui</p>
              <p className="text-2xl font-bold text-gray-900">{stats.today}</p>
            </div>
            <FiCalendar className="text-purple-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Revenu Total</p>
              <p className="text-2xl font-bold text-green-600">{stats.revenue.toLocaleString()} MAD</p>
            </div>
            <FiDollarSign className="text-green-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Commande Moyenne</p>
              <p className="text-2xl font-bold text-gray-900">{Number(stats.average || 0).toFixed(0)} MAD</p>
            </div>
            <FiTrendingUp className="text-orange-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Complétées</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            </div>
            <FiCheckCircle className="text-green-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">En Attente</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <FiClock className="text-yellow-500 text-2xl" />
          </div>
        </div>
      </div>

      {/* Tableau commandes */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Numéro</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Partenaire</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deals</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paiement</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut paiement</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commande / Coupon</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => (
                  <tr key={order._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(order.paymentStatus)}
                        <span className="ml-2 text-sm font-medium text-gray-900">
                          #{order.orderNumber || (order._id ? order._id.slice(-6) : "N/A")}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 mt-1">ID: {order._id}</div>
                        {/* AUTEUR DE LA SAISIE. Present uniquement sur les
                            commandes etablies au guichet : une commande passee
                            par le client sur le site n'a pas d'auteur, et lui en
                            inventer un fausserait le suivi des commerciaux. */}
                        {/* VENDEUR CREDITE. `salesRep` n'est renseigne que
                            lorsqu'il DIFFERE de l'auteur : dans ce cas on
                            affiche les deux, sinon un seul badge. Sans la
                            mention « saisi par », une erreur de saisie ne
                            serait imputable a personne. */}
                        {(order.salesRep || order.createdBy) && (
                          <div className="text-xs mt-1 inline-flex items-center gap-1 bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                            <FiUser size={11} />
                            {(order.salesRep || order.createdBy).name}
                          </div>
                        )}
                        {order.salesRep && order.createdBy && (
                          <div className="text-[11px] text-gray-400 mt-0.5">
                            saisi par {order.createdBy.name}
                          </div>
                        )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {order.customer?.firstName || ""} {order.customer?.lastName || ""}
                        </div>
                        <div className="text-sm text-gray-500">{order.customer?.email}</div>
                        {order.customer?.phone && (
                          <div className="text-xs text-gray-500 flex items-center mt-1">
                            <FiPhone className="mr-1" />
                            {order.customer?.phone}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* PARTENAIRE.
                        `merchants` est une LISTE : une commande peut porter des
                        lignes de plusieurs partenaires, et n'afficher que le
                        premier laisserait croire qu'il est seul concerne.
                        Une commande creee par l'admin sans marchand rattache
                        affiche un tiret plutot qu'un nom invente. */}
                    <td className="px-6 py-4">
                      {Array.isArray(order.merchants) && order.merchants.length ? (
                        <div className="space-y-1">
                          {order.merchants.slice(0, 2).map((m) => (
                            <div key={m.id} className="text-sm font-medium text-gray-900">
                              {m.name}
                            </div>
                          ))}
                          {order.merchants.length > 2 && (
                            <div className="text-xs text-gray-500">
                              +{order.merchants.length - 2} autre{order.merchants.length - 2 > 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>

                    <td className="px-6 py-4">
                      {(() => {
                        const items = Array.isArray(order.items) ? order.items : [];
                        if (!items.length) return <span className="text-sm text-gray-400">—</span>;
                        // v1.398.0 — libelle de la LIGNE d'abord (ce qui a ete vendu, et ce que porte le coupon)
                        const titleOf = (it) => it.title || it.deal?.title || it.dealTitle || 'Deal';
                        const titles = items.map(titleOf);
                        const extra = titles.length - 1;
                        return (
                          <div className="max-w-[260px]" title={titles.join('\n')}>
                            <span className="block text-sm font-medium text-gray-900 truncate">{titles[0]}</span>
                            {extra > 0 && (
                              <span className="inline-block mt-0.5 text-xs text-gray-500">
                                +{extra} autre{extra > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900">{order.total || 0} MAD</span>
                      {Number(order.discountAmount) > 0 && (
                        <span className="block text-xs text-green-700">remise −{Number(order.discountAmount).toFixed(2)}</span>
                      )}
                      {/* EXTRA MAXULA — affiche avec son libelle, pour qu'on
                          sache de quoi il s'agit sans ouvrir la commande.
                          Ce montant n'apparait nulle part cote partenaire. */}
                      {Number(order.extraAmount) > 0 && (
                        <span className="block text-xs text-indigo-700 font-medium">
                          + extra {Number(order.extraAmount).toFixed(2)}
                          {order.extraLabel ? ` · ${order.extraLabel}` : ''}
                        </span>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">{paymentBadge(order.paymentMethod)}</td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(order.paymentStatus)}
                      {/* v1.395.0 client / v1.396.0 serveur — motif d'echec du paiement en ligne */}
                      {order.paymentStatus === 'failed' && order.paymentFailure && (
                        <div
                          className="text-[11px] text-red-600 mt-1 max-w-[190px] whitespace-normal leading-tight"
                          title={[order.paymentFailure.label, order.paymentFailure.detail].filter(Boolean).join(' — ')}
                        >
                          {({
                            DECLINED: 'Carte refusée',
                            CANCELLED: 'Annulé par le client',
                            ERROR: 'Erreur technique',
                            AUTH_REVERSED: 'Autorisation annulée',
                            EXPIRED: 'Non payée en 30 min'
                          })[order.paymentFailure.status] || 'Motif non transmis'}
                        </div>
                      )}
                      {/* QUI a rembourse, ou QUI l'a demande. Sans ces noms, une
                          ligne « Remboursée » ne dit pas d'ou vient la decision. */}
                      {/* QUI a valide. */}
                      {order.refundedBy && (
                        <div className="text-[11px] text-gray-500 mt-1">par {order.refundedBy.name}</div>
                      )}
                      {/* QUI avait demande — affiche AUSSI apres validation.
                          Sans cela, le nom du commercial disparaissait de la
                          ligne des que l'admin approuvait : dans trois mois,
                          impossible de savoir si la demande venait de Hamza ou
                          de Fadoua, alors que l'information est en base. */}
                      {order.refundRequestedBy && (
                        <div className="text-[11px] text-amber-700 mt-1">
                          demandé par {order.refundRequestedBy.name}
                        </div>
                      )}
                      {/* Le motif au survol : il explique la ligne sans
                          l'alourdir. */}
                      {order.refundReason && (
                        <div className="text-[11px] text-gray-400 mt-0.5 max-w-[180px] truncate"
                          title={order.refundReason}>
                          « {order.refundReason} »
                        </div>
                      )}
                      {isPlatformAdmin && order.paymentStatus === 'refunded' && (
                        <button type="button" onClick={() => setRefund({ mode: 'undo', order })}
                          className="text-[11px] text-amber-700 underline mt-1 block">
                          Annuler le remboursement
                        </button>
                      )}
                      {isPlatformAdmin && order.paymentStatus === 'refund_pending' && (
                        <button type="button" onClick={() => setRefund({ mode: 'decide', order })}
                          className="text-[11px] text-red-700 underline mt-1 block">
                          Décider
                        </button>
                      )}
                    </td>

                    {/* v1.407.0 — COMMANDE TERMINEE ? COUPON VALIDE ? */}
                    <td className="px-6 py-4 whitespace-nowrap text-xs">
                      {(() => {
                        const st = String(order.status || '');
                        const lib = ({
                          pending: ['En attente', 'bg-gray-100 text-gray-700'],
                          confirmed: ['Confirmée', 'bg-blue-100 text-blue-700'],
                          processing: ['Préparée', 'bg-indigo-100 text-indigo-700'],
                          preparing: ['Préparée', 'bg-indigo-100 text-indigo-700'],
                          shipped: ['Expédiée', 'bg-indigo-100 text-indigo-700'],
                          delivered: ['Livrée', 'bg-green-100 text-green-700'],
                          completed: ['Terminée', 'bg-green-100 text-green-700'],
                          cancelled: ['Annulée', 'bg-red-100 text-red-700'],
                          refunded: ['Remboursée', 'bg-red-100 text-red-700'],
                        })[st] || [st || '—', 'bg-gray-100 text-gray-700'];
                        const c = order.coupons;
                        let coupon = null;
                        if (c && c.total > 0) {
                          if (c.active === 0 && c.used > 0) {
                            coupon = (
                              <span className="inline-flex items-center gap-1 text-green-700 font-semibold">
                                <FiCheckCircle size={12} /> Coupon validé
                                {c.lastUsedAt ? ` le ${new Date(c.lastUsedAt).toLocaleDateString('fr-FR')}` : ''}
                              </span>
                            );
                          } else if (c.active > 0) {
                            coupon = (
                              <span className={`inline-flex items-center gap-1 font-semibold ${st === 'completed' ? 'text-amber-700' : 'text-gray-500'}`}>
                                <FiClock size={12} /> Coupon non utilisé
                                {c.used > 0 ? ` (${c.used}/${c.total} validé${c.used > 1 ? 's' : ''})` : ''}
                              </span>
                            );
                          } else if (c.expired > 0) {
                            coupon = <span className="text-gray-500 font-semibold">Coupon expiré</span>;
                          }
                        } else if (c && c.cancelled > 0) {
                          coupon = <span className="text-gray-400">Coupon annulé</span>;
                        } else if (order.paymentStatus === 'completed' && c) {
                          coupon = <span className="text-gray-400">Aucun coupon</span>;
                        }
                        return (
                          <div className="space-y-1">
                            <span className={`inline-block px-2 py-0.5 rounded-full font-semibold ${lib[1]}`}>{lib[0]}</span>
                            {coupon && <div>{coupon}</div>}
                            {c && c.list && c.list.filter((v) => v.status !== 'cancelled').length > 0 && (
                              <div className="text-[10px] text-gray-400 font-mono">
                                {c.list.filter((v) => v.status !== 'cancelled').map((v) => v.code).join(' · ')}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.createdAt ? new Date(order.createdAt).toLocaleDateString("fr-FR") : "-"}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {/* BOUTONS D'ACTION.
                          Cinq icones de 18px collees, dont la derniere est
                          destructrice : un agent presse cliquait « Rembourser »
                          en visant « Partager ». Trois changements :
                          - zones tactiles de 36px avec un cadre, au lieu d'une
                            icone nue de 18px ;
                          - PARTAGER remonte juste apres l'oeil, la ou la main
                            va naturellement ;
                          - le REMBOURSEMENT est isole a droite par un trait,
                            avec sa propre couleur et une icone differente
                            (fleche de retour, plus un « X » qui ressemblait au
                            partage). */}
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowDetailsModal(true);
                          }}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-lg border transition border-blue-200 text-blue-600 bg-blue-50 hover:bg-blue-100"
                          title="Voir les détails"
                        >
                          <FiEye size={17} />
                        </button>

                        {/* PARTAGER — remonte en 2e position : c'est le geste
                            le plus frequent apres avoir ouvert la commande, et
                            il s'eloigne ainsi du bouton de remboursement.
                            Icone changee en avion en papier : le symbole de
                            partage precedent se confondait avec les autres. */}
                        {order.orderNumber && (
                          <button
                            type="button"
                            onClick={() => setShareOrderNumber(order.orderNumber)}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border transition border-green-200 text-green-700 bg-green-50 hover:bg-green-100"
                            title="Envoyer au client (WhatsApp, email, impression)"
                          >
                            <FiSend size={16} />
                          </button>
                        )}

                        {/* MODIFIER — corriger une erreur de saisie sans avoir a
                            annuler puis recreer la commande. Le serveur decide
                            de ce qui est reellement modifiable (coupons deja
                            utilises, commande annulee) ; l'ecran s'y adapte. */}
                        <button
                          type="button"
                          onClick={() => setEditOrder({ id: order._id ?? order.id, number: order.orderNumber })}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-lg border transition border-amber-200 text-amber-600 bg-amber-50 hover:bg-amber-100"
                          title="Modifier la commande (client, offres, quantités)"
                        >
                          <FiEdit2 size={16} />
                        </button>

                        {/* DUPLIQUER — le meme client reprend la meme chose. */}
                        <button
                          type="button"
                          onClick={() => duplicateOrder(order)}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-lg border transition border-gray-200 text-gray-600 bg-gray-50 hover:bg-gray-100"
                          title="Dupliquer : nouvelle commande avec le même client et les mêmes offres"
                        >
                          <FiCopy size={16} />
                        </button>

                        {/* ARCHIVER / SUPPRIMER — v1.444.0
                            Une seule entrée, car les deux répondent au même
                            besoin : retirer la ligne de l'écran. C'est le
                            serveur qui dira laquelle est permise. */}
                        <button
                          type="button"
                          onClick={() => openRemove(order)}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-lg border transition border-slate-200 text-slate-500 bg-slate-50 hover:bg-slate-100"
                          title={order.archived ? "Remettre dans la liste" : "Archiver ou supprimer cette commande"}
                        >
                          {order.archived ? <FiInbox size={16} /> : <FiArchive size={16} />}
                        </button>


                        {/* Reçu de virement uploadé par l'acheteur */}
                        {order.paymentProof && (
                          <a
                            href={normalizeImagePath(order.paymentProof)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border transition border-indigo-200 text-indigo-600 bg-indigo-50 hover:bg-indigo-100"
                            title="Voir le reçu de virement"
                          >
                            <FiPaperclip size={16} />
                          </a>
                        )}

                        {/* Confirmer / compléter la commande */}
                        {order.paymentStatus === "pending" && (
                          <button
                            type="button"
                            onClick={() =>
                              handleStatusChange(order.id || order._id, {
                                status: order.status || "confirmed",
                                paymentStatus: "completed",
                                paymentMethod: (order.paymentMethod || order.payment_method || "").toLowerCase() || "cash"
                              })
                            }
                            className="text-green-600 hover:text-green-900"
                            title="Marquer comme payée"
                            disabled={isSubmitting}
                          >
                            <FiCheckCircle size={18} />
                          </button>
                        )}

                        {/* Rembourser / annuler */}
                        {order.paymentStatus !== "refunded" && order.paymentStatus !== "cancelled" && (
                          <button
                            type="button"
                            onClick={() => setRefund({
                              // L'admin decide directement ; le commercial ne
                              // peut que DEMANDER. Une commande deja en attente
                              // ouvre l'ecran de decision.
                              mode: order.paymentStatus === 'refund_pending'
                                ? (isPlatformAdmin ? 'decide' : 'request')
                                : (isPlatformAdmin ? 'decide' : 'request'),
                              order,
                            })}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border transition border-red-300 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-40 ml-2 border-l-4"
                            title={isPlatformAdmin ? 'Rembourser cette commande' : 'Demander un remboursement'}
                            disabled={isSubmitting}
                          >
                            {/* Fleche de retour plutot qu'un « X » : le X se
                                confondait avec les autres icones, alors que
                                c'est le seul bouton qui defait une vente. */}
                            <FiRotateCcw size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-gray-500">
                    <FiShoppingBag className="mx-auto text-4xl mb-2" />
                    <p>Aucune commande trouvée</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t bg-white">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1 || loading}
              className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"
            >
              <FiChevronLeft /> <span>Préc</span>
            </button>

            <button
              type="button"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages || loading}
              className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"
            >
              <span>Suiv</span> <FiChevronRight />
            </button>

            <div className="ml-4 text-sm text-gray-600">
              Page <strong>{page}</strong> / <strong>{totalPages}</strong>
            </div>

            <div className="ml-4 text-sm text-gray-600">
              <span className="mr-2">Afficher</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="border px-2 py-1 rounded"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="ml-2">/ page</span>
            </div>
          </div>

          <div className="text-sm text-gray-600">
            {total > 0 ? `Total: ${total} résultats` : `${filteredOrders.length} affichés`}
          </div>
        </div>
      </div>

      {/* Modal détails */}
      {showCreate && (
        <CreateOrderModal scope="admin" onClose={() => setShowCreate(false)} onCreated={() => fetchOrders(page, limit, searchTerm, statusFilter, dateFilter, merchantFilter)} />
      )}

      {/* Partage d'une commande existante : WhatsApp, email, impression */}
      {shareOrderNumber && (
        <ShareOrderModal
          orderNumber={shareOrderNumber}
          onClose={() => setShareOrderNumber(null)}
        />
      )}

      {duplicate && (
        <CreateOrderModal
          scope="admin"
          initial={duplicate}
          onClose={() => setDuplicate(null)}
          onCreated={() => fetchOrders()}
        />
      )}

      {refund && (
        <RefundModal
          mode={refund.mode}
          order={refund.order}
          onClose={() => setRefund(null)}
          onDone={() => fetchOrders()}
        />
      )}

      {editOrder && (
        <EditOrderModal
          orderId={editOrder.id}
          orderNumber={editOrder.number}
          onClose={() => setEditOrder(null)}
          onSaved={() => fetchOrders()}
        />
      )}

      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Détails de la commande</h2>
                <button
                  type="button"
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Fermer"
                >
                  <FiXCircle size={24} />
                </button>
              </div>

              {/* Boutons admin dans le modal */}
              <div className="flex flex-wrap gap-2 mb-4">
                <InvoiceActions orderNumber={selectedOrder.orderNumber} size="sm" />
                {selectedOrder.orderNumber && (
                  <button
                    type="button"
                    onClick={() => { setShowDetailsModal(false); setShareOrderNumber(selectedOrder.orderNumber); }}
                    className="flex items-center gap-1.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 px-2.5 py-1.5 text-xs"
                  >
                    <FiShare2 size={14} /> Partager
                  </button>
                )}
                {selectedOrder.paymentStatus === "pending" && (
                  <button
                    type="button"
                    onClick={() =>
                      handleStatusChange(selectedOrder.id || selectedOrder._id, {
                        status: selectedOrder.status || "confirmed",
                        paymentStatus: "completed",
                        paymentMethod:
                          (selectedOrder.paymentMethod || selectedOrder.payment_method || "").toLowerCase() || "cash"
                      })
                    }
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                    disabled={isSubmitting}
                  >
                    <FiCheckCircle className="mr-2" />
                    Confirmer le paiement
                  </button>
                )}

                {selectedOrder.paymentStatus !== "refunded" && selectedOrder.paymentStatus !== "cancelled" && (
                  <button
                    type="button"
                    onClick={() =>
                      handleStatusChange(selectedOrder.id || selectedOrder._id, {
                        status: "cancelled",
                        paymentStatus: "refunded"
                      })
                    }
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                    disabled={isSubmitting}
                  >
                    <FiXCircle className="mr-2" />
                    Rembourser
                  </button>
                )}
              </div>

              <div className="space-y-4">
                {/* Infos générales */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Informations commande</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Numéro:</span>
                      <span className="ml-2 font-medium">
                        #{selectedOrder.orderNumber || (selectedOrder._id ? selectedOrder._id.slice(-6) : "")}
                      </span>
                    </div>

                    <div>
                      <span className="text-gray-600">ID:</span>
                      <span className="ml-2 text-xs break-all">{selectedOrder._id}</span>
                    </div>

                    <div>
                      <span className="text-gray-600">Date:</span>
                      <span className="ml-2">
                        {selectedOrder.createdAt ? new Date(selectedOrder.createdAt).toLocaleString("fr-FR") : "-"}
                      </span>
                    </div>

                    <div>
                      <span className="text-gray-600">Méthode paiement:</span>
                      <span className="ml-2">{selectedOrder.paymentMethod || "-"}</span>
                    </div>

                    <div>
                      <span className="text-gray-600">Statut paiement:</span>
                      <span className="ml-2">{getStatusBadge(selectedOrder.paymentStatus)}</span>
                      {selectedOrder.paymentStatus === 'failed' && selectedOrder.paymentFailure && (
                        <div className="mt-2 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">
                          <div className="font-semibold">
                            Motif{selectedOrder.paymentFailure.status ? ` (${selectedOrder.paymentFailure.status})` : ''}
                          </div>
                          <div>{selectedOrder.paymentFailure.label}</div>
                          {selectedOrder.paymentFailure.detail && (
                            <div className="text-xs text-red-600 mt-1">Détail passerelle : {selectedOrder.paymentFailure.detail}</div>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <span className="text-gray-600">Statut commande:</span>
                      <span className="ml-2">{selectedOrder.status || "-"}</span>
                    </div>

                    <div>
                      <span className="text-gray-600">Total:</span>
                      <span className="ml-2 font-semibold">{selectedOrder.total || 0} MAD</span>
                    </div>
                  </div>
                </div>

                {/* ============================================================
                    v1.441.0 — REMBOURSEMENT / ANNULATION : LE DOSSIER COMPLET
                    Jusqu'ici, le motif n'existait qu'en petit texte tronque
                    dans la colonne « Statut paiement » de la liste, et dans la
                    fenetre « Décider » — donc uniquement AVANT la decision.
                    Une fois la commande remboursee, la fiche ne disait plus
                    pourquoi. On regroupe ici les cinq elements qui expliquent
                    une annulation : qui a demande, pourquoi, qui a decide,
                    quand, et la note de l'admin.

                    Le bloc n'apparait QUE s'il y a quelque chose a montrer :
                    l'afficher vide sur une commande normale en ferait du bruit
                    qu'on cesserait de lire.
                   ============================================================ */}
                {(selectedOrder.refundReason
                  || selectedOrder.refundNote
                  || selectedOrder.refundRequestedBy
                  || selectedOrder.refundedBy
                  || selectedOrder.paymentStatus === 'refunded'
                  || selectedOrder.paymentStatus === 'refund_pending') && (
                  <div className={`p-4 rounded-lg border ${
                    selectedOrder.paymentStatus === 'refund_pending'
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <FiAlertTriangle
                        size={16}
                        className={selectedOrder.paymentStatus === 'refund_pending' ? 'text-amber-500' : 'text-red-500'}
                      />
                      {selectedOrder.paymentStatus === 'refund_pending'
                        ? 'Demande de remboursement en attente'
                        : 'Remboursement / annulation'}
                    </h3>

                    <div className="text-sm space-y-2">
                      {selectedOrder.refundRequestedBy && (
                        <p>
                          <span className="text-gray-600">Demandé par :</span>{' '}
                          <span className="font-medium">{selectedOrder.refundRequestedBy.name}</span>
                          {selectedOrder.refundRequestedBy.role
                            ? <span className="text-gray-400 text-xs ml-1">({selectedOrder.refundRequestedBy.role})</span>
                            : null}
                          {selectedOrder.refundRequestedAt
                            ? <span className="text-gray-500 text-xs ml-2">
                                le {new Date(selectedOrder.refundRequestedAt).toLocaleString('fr-FR')}
                              </span>
                            : null}
                        </p>
                      )}

                      {/* LE MOTIF — en entier, jamais tronque. C'est
                          l'information que l'on vient chercher ici. */}
                      {selectedOrder.refundReason ? (
                        <div>
                          <div className="text-gray-600 mb-1">Motif invoqué :</div>
                          <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-gray-900 whitespace-pre-wrap break-words">
                            {selectedOrder.refundReason}
                          </div>
                        </div>
                      ) : (
                        // Distinction volontaire : « aucun motif » n'est pas la
                        // meme chose qu'une donnee manquante. Un remboursement
                        // fait directement par un admin n'en comporte pas, et
                        // l'ecran doit le dire au lieu de laisser un blanc.
                        <p className="text-gray-500 italic">
                          Aucun motif enregistré — remboursement effectué directement par un administrateur,
                          sans demande préalable d'un commercial.
                        </p>
                      )}

                      {selectedOrder.refundedBy && (
                        <p>
                          <span className="text-gray-600">Validé par :</span>{' '}
                          <span className="font-medium">{selectedOrder.refundedBy.name}</span>
                          {selectedOrder.refundedAt
                            ? <span className="text-gray-500 text-xs ml-2">
                                le {new Date(selectedOrder.refundedAt).toLocaleString('fr-FR')}
                              </span>
                            : null}
                        </p>
                      )}

                      {selectedOrder.refundNote && (
                        <div>
                          <div className="text-gray-600 mb-1">Note de l'administrateur :</div>
                          <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-gray-900 whitespace-pre-wrap break-words">
                            {selectedOrder.refundNote}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Client */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Client</h3>
                  <div className="text-sm space-y-1">
                    <p>
                      <span className="text-gray-600">Nom:</span>{" "}
                      {selectedOrder.customer?.firstName || ""} {selectedOrder.customer?.lastName || ""}
                    </p>
                    <p>
                      <span className="text-gray-600">Email:</span> {selectedOrder.customer?.email || "-"}
                    </p>
                    <p>
                      <span className="text-gray-600">Téléphone:</span> {selectedOrder.customer?.phone || "-"}
                    </p>
                  </div>
                </div>

                {/* Articles */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Articles ({selectedOrder.items?.length || 0})</h3>

                  <div className="space-y-2">
                    {selectedOrder.items?.length > 0 ? (
                      selectedOrder.items.map((item, index) => {
                        const dealDoc =
                          item.deal && typeof item.deal === "object"
                            ? item.deal
                            : item.dealId && typeof item.dealId === "object"
                            ? item.dealId
                            : null;
                        const dealId = dealDoc?._id || item.dealId || item.deal || "";
                        const dealSlug = dealDoc?.slug || null;
                        const dealTitle = item.title || dealDoc?.title || item.dealTitle || "Deal";

                        const dealPath = dealSlug ? `/deals/${dealSlug}` : dealId ? `/deals/${dealId}` : "";

                        return (
                          <div key={`${dealId || "item"}-${index}`} className="flex justify-between text-sm border-b pb-2">
                            <div className="max-w-[70%]">
                              <p className="font-medium">
                                {dealPath ? (
                                  <Link
                                    to={dealPath}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-600 hover:underline"
                                  >
                                    {dealTitle}
                                  </Link>
                                ) : (
                                  dealTitle
                                )}
                              </p>
                              <p className="text-gray-600">Quantité: {item.quantity || 0}</p>
                              {item.price != null && (
                                <p className="text-gray-600 text-xs">Prix unitaire: {Number(item.price) || 0} MAD</p>
                              )}
                            </div>

                            <p className="font-semibold">
                              {(Number(item.price) || 0) * (Number(item.quantity) || 1)} MAD
                            </p>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-gray-600">Aucun article</p>
                    )}
                  </div>
                </div>
              </div>
              {/* /space-y */}
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          v1.444.0 — ARCHIVER OU SUPPRIMER
          Une seule fenêtre pour les deux actions : l'utilisateur veut
          « enlever cette ligne », c'est le serveur qui tranche entre les deux
          moyens. Les raisons du refus sont affichées, jamais résumées en
          « action impossible ».
         ============================================================ */}
      {removeFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !removeBusy && setRemoveFor(null)} />
          <div className="relative bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              {/* Le numero de version affiche ici a servi a identifier quel
                  build etait reellement en ligne pendant le diagnostic. Le
                  probleme etant resolu, il n'a plus lieu d'etre : un repere
                  technique n'a rien a faire sous les yeux de l'exploitant au
                  quotidien. */}
              <h3 className="text-lg font-bold text-gray-900">
                {removeFor.archived ? 'Remettre dans la liste' : 'Retirer cette commande'}
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">{removeFor.number}</p>
            </div>

            <div className="p-6 space-y-4">
              {!removeInfo && <p className="text-sm text-gray-500">Vérification en cours…</p>}

              {/* Diagnostic technique, affiché dans la fenêtre plutôt que dans
                  un toast qui disparaît : il faut pouvoir le lire, le copier
                  et le transmettre. */}
              {removeInfo?.erreur && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
                  <div className="text-sm font-semibold text-red-800 mb-1">Le serveur a refusé</div>
                  {/* whitespace-pre-line : le detail technique est sur
                      plusieurs lignes, il doit rester lisible et copiable. */}
                  <p className="text-sm text-red-700 break-words whitespace-pre-line font-mono text-[11px]">
                    {removeInfo.erreur}
                  </p>
                </div>
              )}

              {removeInfo && !removeFor.archived && (
                <>
                  <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-900">
                    <strong>Archiver</strong> retire la commande de la liste sans rien supprimer.
                    Elle reste consultable dans l&apos;onglet « Archivées » et peut revenir en un clic.
                    C&apos;est le choix recommandé, y compris pour une commande remboursée.
                  </div>

                  {removeInfo.canDelete ? (
                    <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-900">
                      <strong>Supprimer définitivement</strong> est possible sur cette commande :
                      elle n&apos;a ni encaissement, ni facture, ni coupon consommé, ni gain partenaire.
                      Le stock réservé sera restitué. Cette action est irréversible.
                    </div>
                  ) : (
                    <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
                      <div className="text-sm font-medium text-gray-800 mb-1">
                        Suppression définitive impossible :
                      </div>
                      <ul className="text-sm text-gray-600 list-disc pl-5 space-y-0.5">
                        {(removeInfo.bloquants || []).length
                          ? removeInfo.bloquants.map((b, i) => <li key={i}>{b}</li>)
                          : <li>Vérification impossible — voir le message ci-dessus.</li>}
                      </ul>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Motif {removeInfo.canDelete && <span className="text-gray-400">(obligatoire pour supprimer)</span>}
                    </label>
                    <input
                      type="text" value={removeReason} maxLength={200}
                      onChange={(e) => setRemoveReason(e.target.value)}
                      placeholder="Ex : erreur de saisie au guichet"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
                    />
                  </div>
                </>
              )}

              {removeInfo && removeFor.archived && (
                <p className="text-sm text-gray-700">
                  Cette commande réapparaîtra dans la liste principale.
                </p>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex flex-wrap justify-end gap-3">
              <button onClick={() => setRemoveFor(null)} disabled={removeBusy}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100">
                Annuler
              </button>

              {removeFor.archived ? (
                <button onClick={() => doArchive(false)} disabled={removeBusy || !removeInfo}
                  className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-black disabled:opacity-60 inline-flex items-center gap-2">
                  <FiInbox size={15} /> Remettre dans la liste
                </button>
              ) : (
                <>
                  {removeInfo?.canDelete && (
                    <button onClick={doDelete} disabled={removeBusy}
                      className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 inline-flex items-center gap-2">
                      <FiTrash2 size={15} /> Supprimer définitivement
                    </button>
                  )}
                  <button onClick={() => doArchive(true)} disabled={removeBusy || !removeInfo}
                    className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-black disabled:opacity-60 inline-flex items-center gap-2">
                    <FiArchive size={15} /> {removeBusy ? 'En cours…' : 'Archiver'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
